/* ============================================================
   SEASONAL STORIES — page runtime.

   Two jobs, both ported from the wireframe:

     1. THE LOADER. Each season opens with a set number of plates and
        reveals one STEP more per press. Both numbers are section
        settings, so the merchant decides them; the script reads them
        off the DOM and never assumes a catalogue size. The button
        removes itself when nothing is held back, which is the only
        way the reader learns how long the season is.

     2. SCROLL REVEAL. The house micro-animation, matched to the
        values the other category pages ship.

   Re-runs on shopify:section:load so it keeps working in the theme
   editor, the way vp-header.js does.
   ============================================================ */
(function () {
  'use strict';

  /* THE TAG IS EMITTED ONCE PER SECTION, and this page carries six of
     them, so without a guard every listener below would be registered
     six times over. Section reloads in the theme editor still re-init
     through the shopify:section:load handler at the foot. */
  if (window.__vpSeasonalStories) return;
  window.__vpSeasonalStories = true;

  /* ----------------------------------------------------------
     THE GAP UNDER THE HEADER IS MEASURED, NOT GUESSED.

     .vp-header is position:fixed: it covers the top of the page
     without occupying it, so the dateline has to be pushed clear by
     hand. The category convention is a flat margin-top:155px, and it
     was tried here first and did not land — that literal is one
     header's resting height less the in-flow band above main, and
     every term in it is an estimate.

     So stop computing it. Compare where the hero ACTUALLY starts
     against where the header ACTUALLY ends, and correct the margin by
     the difference. Self-correcting, and it needs to know nothing
     about the announcement strip or the header's real height.

     Ported from vp-best-sellers.js, which reached this the hard way.
     ---------------------------------------------------------- */
  function syncHeaderOffset() {
    var header = document.querySelector('.vp-header');
    var hero = document.querySelector('.vp-ss .ss-dateline');
    if (!header || !hero) return;

    /* Only correct at rest. The header is ~40px shorter once compact,
       so measuring mid-scroll would set a short margin and the hero
       would jump the moment you moved. Tested on scroll position
       rather than the .clear class, because that class is applied by
       vp-header.js on the same DOMContentLoaded this runs on. */
    if ((window.scrollY || window.pageYOffset || 0) > 60) return;

    var drift = hero.getBoundingClientRect().top -
                header.getBoundingClientRect().bottom;   /* >0 gap, <0 overlap */
    if (Math.abs(drift) < 0.5) return;                   /* already flush */

    var current = parseFloat(window.getComputedStyle(hero).marginTop) || 0;
    document.documentElement.style.setProperty(
      '--ss-header-h', Math.max(0, Math.round(current - drift)) + 'px');
  }

  function watchHeader() {
    if (!document.querySelector('.vp-ss .ss-dateline')) return;  /* not this page */
    syncHeaderOffset();

    /* the wordmark is a webfont: the header's height changes when it
       swaps in, so measure again once fonts have settled */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncHeaderOffset).catch(function () {});
    }
    window.addEventListener('resize', syncHeaderOffset, { passive: true });

    /* Late passes. Layout is still settling at DOMContentLoaded, and
       theme.liquid's own offset script hedges the same way with a
       300ms re-run. Cheap, and it converges: once the two edges are
       flush the function returns without touching anything. */
    window.addEventListener('load', syncHeaderOffset);
    window.setTimeout(syncHeaderOffset, 300);
    window.setTimeout(syncHeaderOffset, 1200);

    /* the header collapses its announcement bar and re-lays out at
       breakpoints; watch it rather than poll */
    var header = document.querySelector('.vp-header');
    if (header && window.ResizeObserver) {
      new window.ResizeObserver(syncHeaderOffset).observe(header);
    }
  }

  function initShop(shop) {
    if (shop.dataset.ssInit) return;
    shop.dataset.ssInit = '1';

    var btn = shop.querySelector('[data-ss-more]');
    if (!btn) return;
    var wrap = btn.parentNode;
    var step = parseInt(shop.getAttribute('data-ss-step'), 10) || 6;

    btn.addEventListener('click', function () {
      var held = shop.querySelectorAll('.plate.is-more');
      for (var i = 0; i < step && i < held.length; i++) {
        held[i].classList.remove('is-more');
      }
      if (!shop.querySelectorAll('.plate.is-more').length) wrap.hidden = true;
    });
  }

  function initWish(root) {
    root.querySelectorAll('[data-ss-wish]').forEach(function (w) {
      if (w.dataset.ssInit) return;
      w.dataset.ssInit = '1';
      w.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        w.classList.toggle('saved');
      });
    });
  }

  function initSwatches(root) {
    root.querySelectorAll('.swatches').forEach(function (g) {
      if (g.dataset.ssInit) return;
      g.dataset.ssInit = '1';
      g.querySelectorAll('.swatch').forEach(function (s) {
        s.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          g.querySelectorAll('.swatch').forEach(function (x) {
            x.classList.remove('on');
            x.setAttribute('aria-pressed', 'false');
          });
          s.classList.add('on');
          s.setAttribute('aria-pressed', 'true');
        });
      });
    });
  }

  /* A scroll-position backstop runs alongside the observer so that
     jumped positions — the almanac's in-page anchors — also reveal.
     An observer alone can miss those. */
  function initReveal(root) {
    var items = [].slice.call(root.querySelectorAll('.vp-reveal'));
    if (!items.length) return;

    function sweep() {
      var h = window.innerHeight;
      items.forEach(function (i) {
        if (i.classList.contains('in')) return;
        if (i.getBoundingClientRect().top < h * 0.94) i.classList.add('in');
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      items.forEach(function (i) { io.observe(i); });
    }

    window.addEventListener('scroll', sweep, { passive: true });
    sweep();
  }

  function init(root) {
    root = root || document;
    watchHeader();
    root.querySelectorAll('.ss-shop').forEach(initShop);
    initWish(root);
    initSwatches(root);
    initReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
